import { useState, useEffect } from 'react';

export function useLocalStorageState<T>(
  key: string,
  defaultValue: T | (() => T)
): [T, (value: T | ((prev: T) => T)) => void] {
  // Get initial value from localStorage or default
  const getStoredValue = () => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) return JSON.parse(stored) as T;
    } catch (e) {
      console.warn(`useLocalStorageState: Error reading key "${key}"`, e);
    }

    return typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
  };

  const [state, setState] = useState<T>(getStoredValue);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
      console.warn(`useLocalStorageState: Error setting key "${key}"`, e);
    }
  }, [key, state]);

  const setValue = (value: T | ((prev: T) => T)) => {
    setState((prev) => (typeof value === 'function' ? (value as (prev: T) => T)(prev) : value));
  };

  return [state, setValue];
}

export default useLocalStorageState;
